import { useEffect } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { IOS_APP_STORE_URL } from "../lib/appLinks";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  ArrowDownToLine,
  ArrowUpRight,
  Smartphone,
  ScanLine,
  ShieldCheck,
  Sparkles,
} from "lucide-react";

// Die APK liegt als GitHub-Release-Asset, nicht im Repo: Die Binärdatei gehört weder in die
// Git-History noch in jeden Docker-Build. "latest" zeigt immer auf das neueste Release,
// der Link muss also bei einer neuen Version nicht angefasst werden.
const APK_HREF =
  "https://github.com/MakeIt-Media/booking-releases/releases/latest/download/make-it-booking.apk";
const PAGE_PATH = "/android-download";

const steps = [
  {
    number: "01",
    title: "APK herunterladen",
    text: "Tippe auf den Download-Button und öffne anschließend die geladene Datei.",
    visual: "download",
  },
  {
    number: "02",
    title: "Quelle erlauben",
    text: "Wenn Android blockiert, erlaube Chrome oder deiner Dateien-App einmalig die Installation.",
    visual: "permission",
  },
  {
    number: "03",
    title: "App installieren",
    text: "Kehre zur APK zurück, tippe auf Installieren und öffne Make It Booking.",
    visual: "install",
  },
];

const faqs = [
  {
    question: "Wo finde ich die heruntergeladene APK?",
    answer: "Öffne in Chrome das Download-Menü oder die Dateien-App und tippe auf make-it-booking.apk.",
  },
  {
    question: "Warum wird die Installation blockiert?",
    answer: "Android benötigt ab Version 8 eine einmalige Freigabe für die App, aus der du die APK öffnest.",
  },
  {
    question: "Kann ich die Berechtigung wieder deaktivieren?",
    answer: "Ja. Öffne nach der Installation die Sicherheitseinstellungen und schalte die Quelle wieder aus.",
  },
  {
    question: "Die App lässt sich trotzdem nicht installieren.",
    answer: "Prüfe, ob bereits eine ältere Version installiert ist, genug Speicher frei ist und die Datei vollständig geladen wurde.",
  },
];

function SectionEyebrow({ children, dark = false }) {
  return (
    <p className={`mb-5 text-[11px] font-semibold uppercase tracking-[0.32em] ${dark ? "text-[#F05A8A]" : "text-[#F02672]"}`}>
      {children}
    </p>
  );
}

function GradientText({ children }) {
  return (
    <span className="landing-gradient-text inline bg-gradient-to-r from-[#F05A8A] via-[#EC2B72] to-[#C83CE0] bg-clip-text pb-[0.18em] pt-[0.06em] pl-[0.02em] pr-[0.08em] italic font-light leading-[1.16] text-transparent">
      {children}
    </span>
  );
}

function useLandingReveal() {
  useEffect(() => {
    const elements = Array.from(document.querySelectorAll("[data-reveal]"));

    if (!elements.length) return undefined;

    if (!("IntersectionObserver" in window)) {
      elements.forEach((element) => element.classList.add("is-visible"));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: "0px 0px -12% 0px", threshold: 0.12 }
    );

    elements.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, []);
}

function DevicePreview({ type }) {
  return (
    <div className="relative mx-auto h-44 w-28 rounded-[1.6rem] border-[5px] border-[#171014] bg-[#FFFBFC] p-2 shadow-[0_18px_38px_-18px_rgba(42,23,32,0.55)] sm:h-48 sm:w-32">
      <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-[#171014] opacity-80" />
      {type === "download" && (
        <div className="space-y-2 rounded-xl bg-white p-2 text-[7px] text-[#6F6067] shadow-sm">
          <div className="flex items-center gap-1 font-semibold text-[#171014]">
            <img src="/images/mib-logo-120.png" alt="" className="h-4 w-4 rounded object-contain" aria-hidden="true" />
            Booking.apk
          </div>
          <div className="h-1.5 rounded-full bg-[#F9DCE8]"><div className="h-1.5 w-3/4 rounded-full bg-[#EC2B72]" /></div>
          <span>Download abgeschlossen</span>
        </div>
      )}
      {type === "permission" && (
        <div className="space-y-2 rounded-xl bg-white p-2 text-[7px] text-[#6F6067] shadow-sm">
          <div className="font-semibold text-[#171014]">Unbekannte Apps</div>
          <div className="rounded-lg bg-[#F3EDF0] p-2">Chrome</div>
          <div className="flex items-center justify-between font-semibold text-[#171014]">
            <span>Erlauben</span>
            <span className="h-3 w-6 rounded-full bg-[#EC2B72] p-0.5"><span className="ml-3 block h-2 w-2 rounded-full bg-white" /></span>
          </div>
        </div>
      )}
      {type === "install" && (
        <div className="mt-4 rounded-xl bg-white p-3 text-center shadow-sm">
          <img src="/images/mib-logo-120.png" alt="" className="mx-auto mb-2 h-8 w-8 rounded-xl object-contain" aria-hidden="true" />
          <div className="text-[8px] font-semibold text-[#171014]">App installieren?</div>
          <div className="mt-3 flex justify-center gap-2 text-[7px] font-semibold">
            <span className="text-[#7B6A72]">Abbrechen</span>
            <span className="text-[#EC2B72]">Installieren</span>
          </div>
        </div>
      )}
      <div className="absolute bottom-2 left-1/2 h-1 w-8 -translate-x-1/2 rounded-full bg-[#171014] opacity-70" />
    </div>
  );
}

export default function AndroidDownload() {
  useLandingReveal();

  // Der QR-Code muss absolut sein, damit er auf dem Handy auflösbar ist.
  const pageUrl =
    typeof window !== "undefined" ? `${window.location.origin}${PAGE_PATH}` : PAGE_PATH;

  const smoothScroll = (e) => {
    const href = e.currentTarget.getAttribute("href");
    if (!href || !href.startsWith("#")) return;
    e.preventDefault();
    const target = document.querySelector(href);
    if (!target) return;
    const top = target.getBoundingClientRect().top + window.scrollY - 80;
    window.scrollTo({ top, behavior: "smooth" });
  };

  return (
    <div className="landing-page min-h-screen overflow-hidden bg-[#FFFBFC] text-[#111014]">
      <header className="fixed left-0 right-0 top-0 z-50 border-b border-[#2A1720]/10 bg-[#FFFBFC]/88 shadow-[0_12px_40px_-34px_rgba(42,23,32,0.45)] backdrop-blur-2xl">
        <div className="mx-auto flex h-14 max-w-[1368px] items-center justify-between gap-4 px-4 sm:h-16 sm:px-6 md:px-10 lg:px-14">
          <Link to="/" className="flex items-center" aria-label="Make It Booking Startseite">
            <img src="/images/mib-logo-full.png" alt="Make It Booking Logo" className="h-10 w-auto object-contain sm:h-11" />
          </Link>
          <span className="inline-flex h-9 items-center gap-2 rounded-full border border-[#2A1720]/15 bg-white px-4 text-xs font-medium text-[#43353B] shadow-sm sm:text-sm">
            <ShieldCheck className="h-4 w-4 text-[#F02672]" />
            Sichere Verbindung
          </span>
        </div>
      </header>

      <section className="px-4 pb-12 pt-24 sm:px-6 md:px-10 md:pb-16 md:pt-28 lg:px-14">
        <div className="mx-auto max-w-[1440px]" data-reveal>
          <div className="dot-grid relative overflow-hidden rounded-[2rem] bg-[#171014] px-6 py-12 text-white sm:p-12 md:rounded-[2.5rem] md:p-16 lg:p-20">
            <div className="pulse-glow absolute -right-24 -top-24 h-[420px] w-[420px] rounded-full bg-[#D72D6B]/35 blur-[110px]" />
            <div className="pulse-glow absolute -bottom-24 -left-16 h-[380px] w-[380px] rounded-full bg-[#F05A8A]/20 blur-[100px] [animation-delay:1.4s]" />

            <div className="relative grid items-center gap-10 lg:grid-cols-12 lg:gap-12">
              <div className="lg:col-span-7">
                <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3.5 py-1.5 backdrop-blur-md">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#F05A8A] opacity-70" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-[#F05A8A]" />
                  </span>
                  <span className="text-xs font-semibold tracking-wide text-white/85">Android Download</span>
                </div>

                <h1 className="mt-6 max-w-3xl text-4xl font-semibold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-[58px]">
                  Make It Booking <GradientText>auf Android</GradientText> installieren
                </h1>
                <p className="mt-7 max-w-xl text-base leading-relaxed text-white/76 sm:text-lg">
                  <span className="font-semibold text-white">Direkt laden. Drei kurze Schritte. Fertig.</span> Lade die aktuelle App herunter und folge der Anleitung. Die Installation dauert in der Regel weniger als zwei Minuten.
                </p>

                <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
                  <a
                    href={APK_HREF}
                    className="btn-shine inline-flex h-14 w-full items-center justify-center rounded-full bg-[#EC2B72] px-7 text-[15px] font-medium text-white shadow-[0_14px_40px_-16px_rgba(236,43,114,0.85)] transition-colors hover:bg-[#D72D6B] sm:w-auto"
                    data-testid="hero-download"
                  >
                    <ArrowDownToLine className="mr-2 h-4 w-4" />
                    APK herunterladen
                  </a>
                  <a
                    href="#installation"
                    onClick={smoothScroll}
                    className="inline-flex h-14 w-full items-center justify-center rounded-full border border-white/20 bg-white/5 px-7 text-[15px] font-medium text-white/90 backdrop-blur transition-colors hover:bg-white/15 hover:text-white sm:w-auto"
                  >
                    Anleitung ansehen
                    <ArrowUpRight className="ml-2 h-4 w-4" />
                  </a>
                </div>
                <a href={IOS_APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex items-center gap-1 text-sm text-white/75 underline-offset-4 hover:text-white hover:underline">
                  iPhone oder iPad? Im App Store öffnen <ArrowUpRight className="h-4 w-4" />
                </a>

                <div className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm">
                  <div className="flex items-center gap-2 text-white/70">
                    <ShieldCheck className="h-4 w-4 text-[#F05A8A]" strokeWidth={3} />
                    Offizielle Quelle
                  </div>
                  <div className="flex items-center gap-2 text-white/70">
                    <Smartphone className="h-4 w-4 text-[#F05A8A]" strokeWidth={2} />
                    Android 9 oder neuer
                  </div>
                  <div className="flex items-center gap-2 text-white/70">
                    <Sparkles className="h-4 w-4 text-[#F05A8A]" strokeWidth={2} />
                    Version 1.0.7
                  </div>
                </div>
              </div>

              {/* Auf Mobilgeräten ausgeblendet: Wer die Seite schon am Handy liest, braucht den Code nicht. */}
              <div className="hidden lg:col-span-5 lg:flex lg:justify-center">
                <div className="relative animate-[float_6s_ease-in-out_infinite]">
                  <div className="rounded-[2rem] bg-white p-7 text-center shadow-[0_30px_70px_-20px_rgba(0,0,0,0.65)]">
                    <QRCodeSVG
                      value={pageUrl}
                      size={208}
                      level="H"
                      bgColor="#FFFFFF"
                      fgColor="#171014"
                      imageSettings={{
                        src: "/images/mib-logo-120.png",
                        height: 44,
                        width: 44,
                        excavate: true,
                      }}
                    />
                    <div className="mt-6 flex items-center justify-center gap-2 text-[#171014]">
                      <ScanLine className="h-4 w-4 text-[#D72D6B]" />
                      <span className="text-sm font-medium tracking-tight">Mit dem Handy scannen</span>
                    </div>
                    <p className="mx-auto mt-1.5 max-w-[13rem] text-xs leading-relaxed text-[#7B6A72]">
                      Öffnet diese Seite direkt auf deinem Android-Gerät.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="installation" className="scroll-mt-24 px-6 py-20 md:px-10 md:py-28 lg:px-14">
        <div className="mx-auto max-w-[1280px]">
          <div className="max-w-2xl" data-reveal>
            <SectionEyebrow>Schritt für Schritt</SectionEyebrow>
            <h2 className="text-3xl font-semibold leading-[1.1] tracking-tight sm:text-4xl lg:text-5xl">
              So installierst du <GradientText>die APK.</GradientText>
            </h2>
            <p className="mt-7 text-lg leading-relaxed text-[#6F6067]">
              Die Bezeichnungen können je nach Hersteller leicht abweichen. Die Berechtigung wird nur für die verwendete Quelle, zum Beispiel Chrome, benötigt.
            </p>
          </div>

          <div className="mt-12 grid gap-5 md:grid-cols-3" data-reveal>
            {steps.map((step) => (
              <article
                key={step.number}
                className="glow-card rounded-3xl border border-[#2A1720]/10 bg-white p-6 shadow-[0_18px_60px_-40px_rgba(42,23,32,0.35)]"
              >
                <div className="mb-8 flex items-center justify-between">
                  <span className="grid h-9 w-9 place-items-center rounded-full bg-[#EC2B72] text-xs font-semibold text-white shadow-[0_10px_24px_-12px_rgba(236,43,114,0.9)]">
                    {step.number}
                  </span>
                  <Smartphone className="h-4 w-4 text-[#C4B5BD]" />
                </div>
                <DevicePreview type={step.visual} />
                <h3 className="mt-8 text-lg font-medium tracking-tight text-[#171014]">{step.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#6F6067]">{step.text}</p>
              </article>
            ))}
          </div>

          <div className="glow-card relative mt-6 overflow-hidden rounded-3xl bg-[#171014] p-8 text-white md:p-10" data-reveal>
            <div className="absolute -right-12 -top-12 h-48 w-48 rounded-full bg-[#D72D6B]/45 blur-3xl" />
            <div className="relative flex flex-col gap-4 md:flex-row md:items-start md:gap-6">
              <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#D72D6B]/20">
                <ShieldCheck className="h-5 w-5 text-[#F05A8A]" />
              </span>
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#F05A8A]">Sicherheitshinweis</p>
                <p className="max-w-3xl text-base leading-relaxed text-white/80">
                  Lade die APK nur von dieser offiziellen Make-It-Booking-Seite. Google Play Protect sollte aktiv bleiben. Nach der Installation kannst du „Aus dieser Quelle zulassen“ wieder deaktivieren.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="faq" className="px-6 pb-20 md:px-10 md:pb-28 lg:px-14">
        <div className="mx-auto grid max-w-[1200px] items-start gap-12 lg:grid-cols-12 lg:gap-20">
          <div className="lg:sticky lg:top-24 lg:col-span-5 lg:self-start" data-reveal>
            <SectionEyebrow>Häufige Fragen</SectionEyebrow>
            <h2 className="text-3xl font-semibold leading-[1.1] tracking-tight sm:text-4xl lg:text-5xl">
              Falls etwas <GradientText>hakt.</GradientText>
            </h2>
            <p className="mt-7 text-lg leading-relaxed text-[#6F6067]">
              Die häufigsten Stolpersteine bei der Installation und wie du sie in wenigen Sekunden löst.
            </p>
          </div>
          <div className="lg:col-span-7" data-reveal>
            <Accordion type="single" collapsible className="space-y-3">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={faq.question}
                  value={`faq-${index}`}
                  className="glow-card rounded-2xl border border-[#2A1720]/10 bg-white px-5 transition-all data-[state=open]:border-[#D72D6B]/30 data-[state=open]:shadow-[0_12px_36px_-18px_rgba(215,45,107,0.25)] md:px-7"
                  data-testid={`faq-${index}`}
                >
                  <AccordionTrigger className="faq-trigger py-5 text-left text-lg font-medium tracking-tight text-[#171014] transition-colors hover:text-[#D72D6B] hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 pr-4 text-base leading-relaxed text-[#6F6067]">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <section className="px-6 pb-20 md:px-10 md:pb-28 lg:px-14">
        <div
          className="mx-auto max-w-[1200px] overflow-hidden rounded-[2rem] bg-gradient-to-br from-[#FFE1EC] via-[#FFF7FA] to-[#F8EEF2] p-8 text-center shadow-[0_20px_80px_-50px_rgba(42,23,32,0.45)] md:p-14"
          data-reveal
        >
          <SectionEyebrow>Bereit?</SectionEyebrow>
          <h2 className="mx-auto max-w-3xl text-3xl font-semibold leading-[1.1] tracking-tight sm:text-4xl lg:text-5xl">
            Hol dir Make It Booking <GradientText>auf dein Android-Handy.</GradientText>
          </h2>
          <p className="mx-auto mt-7 max-w-2xl text-lg leading-relaxed text-[#6F6067]">
            Termine, Kunden und Nachrichten immer dabei, auch unterwegs zwischen zwei Behandlungen.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a
              href={APK_HREF}
              className="btn-shine inline-flex h-14 items-center justify-center rounded-full bg-[#171014] px-8 text-[15px] font-medium text-white shadow-[0_12px_30px_-16px_rgba(215,45,107,0.6)] transition-colors hover:bg-[#2A1720]"
              data-testid="cta-download"
            >
              <ArrowDownToLine className="mr-2 h-4 w-4" />
              APK herunterladen
            </a>
            <Link
              to="/support"
              className="inline-flex h-14 items-center justify-center rounded-full border border-[#2A1720]/15 bg-white/80 px-8 text-[15px] font-medium text-[#171014] backdrop-blur transition-colors hover:bg-white"
            >
              Support kontaktieren
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#2A1720]/10 bg-[#F8EEF2]/60 px-6 py-14 md:px-10 lg:px-14">
        <div className="mx-auto max-w-[1440px]">
          <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
            <Link to="/" className="flex items-center">
              <img src="/images/mib-logo-full.png" alt="Make It Booking Logo" className="h-11 w-auto object-contain" />
            </Link>
            <nav className="flex flex-wrap gap-x-7 gap-y-3 text-sm text-[#6F6067]">
              <a href="#installation" onClick={smoothScroll} className="transition-colors hover:text-[#171014]">Anleitung</a>
              <a href="#faq" onClick={smoothScroll} className="transition-colors hover:text-[#171014]">FAQ</a>
              <Link to="/support" className="transition-colors hover:text-[#171014]">Support</Link>
              <Link to="/datenschutz" className="transition-colors hover:text-[#171014]">Datenschutz</Link>
              <Link to="/impressum" className="transition-colors hover:text-[#171014]">Impressum</Link>
            </nav>
            <p className="text-xs text-[#6F6067]">© {new Date().getFullYear()} Make It Booking. Alle Rechte vorbehalten.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
